<script setup lang="ts">
import BaseLayout from "@/layouts/BaseLayout.vue";
import {computed, ref} from "vue";
import SearchField from "@/components/forms/SearchField.vue";
import {DataTableHeader} from "vuetify/framework";
import SortField from "@/components/forms/SortField.vue";
import {CapacityFramework, Pagination, PaginationFilters} from "@/types";
import {router} from "@inertiajs/vue3";
import {route} from "ziggy-js";
import IconTooltip from "@/components/tooltips/IconTooltip.vue";
import {useDate} from "@/composables/date";
import OpenChip from "@/components/chips/OpenChip.vue";
import CompletedChip from "@/components/chips/CompletedChip.vue";
import ClosedChip from "@/components/chips/ClosedChip.vue";
import {AssessmentSummary} from "@/types/assessments";
import DueDateTooltip from "@/components/tooltips/DueDateTooltip.vue";
import {useNumber} from "@/composables/number";
import CapacityFrameworkLayout from "@/layouts/CapacityFrameworkLayout.vue";
import VersionChip from "@/components/chips/VersionChip.vue";

defineOptions({layout: [BaseLayout, CapacityFrameworkLayout]})

const {shortDate} = useDate()
const {percentage} = useNumber()

interface Props {
    title: string
    capacityFramework: CapacityFramework
    category: 'overview'|'users'|'assessments'|'progression'|'public'|'versions'
    filter: 'own' | 'starred' | 'public'
    items: Pagination<AssessmentSummary>
    filters: PaginationFilters
}

const props = defineProps<Props>();

const search = ref<string | null>(props.filters.search)
const sortBy = ref<string>(props.filters.sort_by)
const sortDir = ref<'asc' | 'desc'>(props.filters.sort_dir)
const page = ref<number>(props.filters.page)

const lastPage = computed(() => {
    return props.items.last_page || 1;
});

const sortItems: Array<{ title: string; value: string }> = [
    {
        title: 'Title',
        value: 'title'
    },
    {
        title: 'Created date',
        value: 'created_at'
    },
    {
        title: 'Updated date',
        value: 'updated_at'
    },
    {
        title: 'Due date',
        value: 'due_date'
    },
    {
        title: 'Starred',
        value: 'is_starred'
    }
]

const headers: Array<DataTableHeader> = [
    {
        title: 'Title',
        value: 'title',
        sortable: false,
        align: 'start',
        width: '70%'
    },
    {
        title: 'Completion',
        value: 'completion',
        sortable: false,
        align: 'start',
        width: '30%'
    },
]

const loadItems = () => {
    const params: Record<string, string | null> = {
        search: search.value,
        sort_by: sortBy.value,
        sort_dir: sortDir.value,
        page: page.value.toString()
    };

    // Remove empty parameters
    Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null) {
            delete params[key];
        }
    });

    const uri = route('capacity-frameworks.assessments.index', {capacityFramework: props.capacityFramework.id, filter: props.filter});

    router.get(
        uri,
        params,
        {
            preserveState: true,
        }
    );
}

const goToAssessment = (id: number) => {
    router.get(route('assessments.show', {assessment: id}), {});
};

</script>

<template>
    <v-row class="mt-3 bg-grey-lighten-5 border-t-sm border-b-sm">
        <v-col
            cols="12"
            md="9"
            class="py-4"
        >
            <search-field
                v-model="search"
                @click="loadItems"
            />
        </v-col>
        <v-col
            cols="12"
            md="3"
            class="py-4"
        >
            <sort-field
                v-model:sort-by="sortBy"
                v-model:sort-dir="sortDir"
                :items="sortItems"
                @update:sort-by="loadItems"
                @update:sort-dir="loadItems"
            />
        </v-col>
    </v-row>
    <v-row>
        <v-col
            cols="12"
            class="py-0"
        >
            <v-data-table
                :headers="headers"
                :items="props.items.data"
                :items-per-page="props.items.per_page"
                :page="props.items.current_page"
                disable-sort
                hide-default-header
                hide-default-footer
                no-data-text="No results found."
            >
                <template #no-data>
                    <v-sheet class="mt-10">
                        <v-row
                            class="fill-height"
                            align="center"
                            justify="center"
                        >
                            <v-col
                                cols="12"
                                class="text-center"
                            >
                                <v-avatar size="128">
                                    <v-icon
                                        size="128"
                                        color="grey-lighten-2"
                                    >
                                        mdi-emoticon-sad-outline
                                    </v-icon>
                                </v-avatar>
                                <h2 class="text-h5 font-weight-bold">
                                    No assessments found.
                                </h2>
                                <p>
                                    Try adjusting your search or category criteria.
                                </p>
                            </v-col>
                        </v-row>
                    </v-sheet>
                </template>
                <template #[`item.title`]="{ item }">
                    <v-list-item>
                        <template #prepend>
                            <v-icon
                                v-if="item.is_starred"
                                size="small"
                            >
                                mdi-star
                            </v-icon>
                        </template>
                        <v-list-item-title
                            class="text-body-2 font-weight-bold"
                        >
                            <span
                                class="custom-link"
                                @click="goToAssessment(item.id)"
                            >
                                {{ item.title }}
                            </span>
                            <icon-tooltip
                                v-if="!item.is_public"
                                icon="mdi-lock-outline"
                                class="ml-1"
                                size="small"
                            >
                                Anonymized answers - The administrator cannot see the answers of the resource.
                            </icon-tooltip>
                        </v-list-item-title>
                        <v-list-item-subtitle>
                            <v-icon
                                class="mr-1"
                                size="x-small"
                            >
                                mdi-home-group
                            </v-icon>
                            {{ item.team.name }}
                        </v-list-item-subtitle>
                        <template #append>
                            <due-date-tooltip
                                v-if="item.due_date"
                                :date="item.due_date"
                                :completed="item.status === 'completed'"
                            >
                                {{ shortDate(item.due_date) }}
                            </due-date-tooltip>
                            <version-chip>
                                {{ item.capacity_framework_version_id }}
                            </version-chip>
                            <open-chip v-if="item.status === 'open'" />
                            <completed-chip v-else-if="item.status === 'completed'" />
                            <closed-chip v-else-if="item.status === 'closed'" />
                        </template>
                    </v-list-item>
                </template>
                <template #[`item.completion`]="{ item }">
                    <div
                        v-if="item.capacity_indicators_count > 0"
                        class="d-flex flex-row align-center w-100"
                    >
                        <div class="text-grey-darken-3 text-sm mr-2 text-no-wrap">
                            {{ item.answers_count }}/{{ item.capacity_indicators_count }} complete
                        </div>
                        <v-progress-linear
                            :model-value="(item.answers_count / item.capacity_indicators_count) * 100"
                            color="primary"
                            class="flex-grow-1"
                            height="6"
                            rounded
                            rounded-bar
                        />
                        <div
                            class="text-grey-darken-3 text-sm ml-2"
                        >
                            {{ percentage(item.answers_count/item.capacity_indicators_count) }}
                        </div>
                    </div>
                </template>
            </v-data-table>
            <v-row
                v-if="items.total > 0"
                class="justify-center mt-5"
            >
                <v-pagination
                    v-model="page"
                    :length="lastPage"
                    density="compact"
                    active-color="primary"
                    @update:model-value="loadItems"
                />
            </v-row>
        </v-col>
    </v-row>
</template>

<style scoped>
:deep(.v-list-item__prepend) {
    width: 24px;
}

:deep(.text-sm) {
    font-size: 0.75rem !important;
}
</style>
